import type {
  Insets,
  LayoutData,
  PortAdjustmentPolicy,
  TimeSpan,
} from '@ardoq/yfiles';
import { dispatchAction } from '@ardoq/rxbeach';
import { logError } from '@ardoq/logging';
import { ViewIds } from '@ardoq/api-types';
import { OnLayoutGraphArgs } from './onLayoutGraph';
import { runWebWorkerLayout } from 'tabview/graphComponent/runWebWorkerLayout';
import { notifyViewLoading } from 'tabview/actions';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { startViewWork } from 'tabview/activeViewsStatus/viewStatusReporter';

interface RunLayoutArgs extends OnLayoutGraphArgs {
  layoutData: LayoutData;
  onLayoutGraph: (args: OnLayoutGraphArgs) => void;
  performanceTrackingId?: string | null;
  recordRenderComplete: (performanceTrackingId: string) => void;
  duration?: TimeSpan;
  animateViewport?: boolean;
  updateContentRect?: boolean;
  targetBoundsInsets?: Insets;
  portAdjustmentPolicy?: PortAdjustmentPolicy;
  viewId: ViewIds;
  viewInstanceId: string;
  viewStatusReporter: ViewStatusReporter;
}

const runLayout = ({
  graphComponent,
  layoutDescriptor,
  layoutData,
  onLayoutGraph,
  performanceTrackingId,
  recordRenderComplete,
  hasGraphUpdate,
  previousLayoutDescriptor,
  duration,
  animateViewport,
  updateContentRect,
  targetBoundsInsets,
  portAdjustmentPolicy,
  viewId,
  viewInstanceId,
  viewStatusReporter,
}: RunLayoutArgs) => {
  const layoutToken = startViewWork(viewStatusReporter, 'layout');
  return new Promise<void>(resolve => {
    runWebWorkerLayout({
      graphComponent,
      layoutDescriptor,
      layoutData,
      duration,
      animateViewport,
      updateContentRect,
      targetBoundsInsets,
      portAdjustmentPolicy,
      viewId,
      viewInstanceId,
    })
      .then(() => {
        onLayoutGraph({
          graphComponent,
          hasGraphUpdate,
          layoutDescriptor,
          previousLayoutDescriptor,
        });
        setTimeout(() => {
          dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: false }));
          viewStatusReporter.reportWorkFinished(layoutToken);
          if (performanceTrackingId) {
            recordRenderComplete(performanceTrackingId);
          }
        });
        resolve();
      })
      .catch((error: unknown) => {
        logError(
          error instanceof Error ? error : Error(String(error)),
          'Layout failed to run',
          {
            viewId,
            fingerprint: ['layout-run-failed'],
          }
        );
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: false }));
        viewStatusReporter.reportWorkFinished(layoutToken);
        resolve();
      });
  });
};
export { runLayout };
